import { CompletionShell } from "@/components/pages/SuccessPage";
import { useScreenerCopy } from "@/features/i18n/screenerCopy";
import { InviteQrMark } from "@/features/qr-code/components/QrCodeRoute";
import { Button } from "@patient-watch/ui/button";
import type { ScreenerOutcomeT } from "@patient-watch/types";
import { Link, useRouter } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useMemo } from "react";

interface ScreenerResultProps {
    screenerId: string;
    outcome: ScreenerOutcomeT;
    title: string;
    body: string;
    /** Where an eligible patient signs up. Null when the screener was opened without a sign-up link. */
    signupSearch: Record<string, unknown> | null;
    /** The incoming sign-up search, kept on "Start again". */
    signup?: Record<string, unknown>;
}

/** The end of a screener, in the same completion shell as a finished form. */
function ScreenerResult({ screenerId, outcome, title, body, signupSearch, signup }: ScreenerResultProps) {
    const copy = useScreenerCopy();
    const router = useRouter();
    const isEligible = outcome === "eligible";

    const signupUrl = useMemo(
        () =>
            signupSearch
                ? `${window.location.origin}${router.buildLocation({ to: "/signup", search: signupSearch as never }).href}`
                : null,
        [router, signupSearch]
    );

    if (!isEligible) {
        return (
            <CompletionShell title={title} description={body} icon={Info} pinFooter>
                <Button asChild variant="link" className="mt-2">
                    <Link
                        to="/screen/$screenerId"
                        params={{ screenerId }}
                        search={signup ? { signup } : {}}
                        data-testid="screener-start-again"
                    >
                        {copy.startAgain}
                    </Link>
                </Button>
            </CompletionShell>
        );
    }

    // Opened from the form builder rather than a template link: nothing to sign up to.
    if (!signupSearch || !signupUrl) {
        return <CompletionShell title={title} description={body} pinFooter />;
    }

    return (
        <CompletionShell
            title={title}
            description={body}
            pinFooter
            primaryButton={{ text: copy.continueToSignUp, to: "/signup", search: signupSearch }}
        >
            {/* Someone screening on a computer can carry on from their phone. A phone uses the button. */}
            <div className="hidden md:flex flex-col items-center gap-3 mt-2" data-testid="screener-signup-qr">
                <InviteQrMark destinationUrl={signupUrl} title={title} variant="compact" />
                <p className="max-w-sm text-slate-600 dark:text-slate-400 text-sm text-balance">{copy.scanOnPhone}</p>
            </div>
        </CompletionShell>
    );
}

export default ScreenerResult;
